import React, { Suspense, lazy, useEffect } from 'react';
import { Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { SiteLayout } from '@/components/layout/SiteLayout';
import { AppLayout } from '@/components/app/AppLayout';
import { OptimizedProviders } from '@/components/performance/OptimizedProviders';
import { SkipLink } from '@/components/ui/skip-link';
import { adaptiveLoader } from '@/utils/adaptiveLoader';
import { PageSkeleton } from '@/components/ui/PageSkeleton';

// CRITICAL: Immediate load for homepage only
import OptimizedHomePage from './pages/OptimizedHomePage';

const SmartPageLoader: React.FC<{ variant?: 'default' | 'products' | 'auth' | 'dashboard' | 'content' }> = ({ 
  variant = 'default' 
}) => (
  <PageSkeleton variant={variant} />
);

const createLazy = (
  importFn: () => Promise<any>,
  name: string,
  priority: 'immediate' | 'high' | 'normal' | 'low' = 'normal'
) => lazy(() => adaptiveLoader.loadChunk(importFn, name, priority));

// ============ VITRINE: Public pages ============
const AuthPage = createLazy(() => import('./pages/AuthPage'), 'AuthPage', 'high');
const AuthCallback = createLazy(() => import('./pages/AuthCallback'), 'AuthCallback');
const SolutionPage = createLazy(() => import('./pages/SolutionPage'), 'SolutionPage');
const ProducteursPage = createLazy(() => import('./pages/ProducteursPage'), 'ProducteursPage');
const AcheteursPage = createLazy(() => import('./pages/AcheteursPage'), 'AcheteursPage');
const PartenairesPage = createLazy(() => import('./pages/PartenairesPage'), 'PartenairesPage');
const CooperativesPage = createLazy(() => import('./pages/CooperativesPage'), 'CooperativesPage');
const Contact = createLazy(() => import('./pages/Contact'), 'Contact', 'low');
const About = createLazy(() => import('./pages/About'), 'About', 'low');
const FAQ = createLazy(() => import('./pages/FAQ'), 'FAQ', 'low');
const Sitemap = createLazy(() => import('./pages/Sitemap'), 'Sitemap', 'low');
const LegalNotice = createLazy(() => import('./pages/LegalNotice'), 'LegalNotice', 'low');
const Privacy = createLazy(() => import('./pages/Privacy'), 'Privacy', 'low');
const TermsOfUse = createLazy(() => import('./pages/TermsOfUse'), 'TermsOfUse', 'low');
const Cookies = createLazy(() => import('./pages/Cookies'), 'Cookies', 'low');
const TermsOfSale = createLazy(() => import('./pages/TermsOfSale'), 'TermsOfSale', 'low');
const RefundPolicy = createLazy(() => import('./pages/RefundPolicy'), 'RefundPolicy', 'low');
const NotFoundPage = createLazy(() => import('./pages/NotFoundPage'), 'NotFoundPage', 'low');

// ============ APP: Business Platform ============
const AppLogin = createLazy(() => import('./pages/app/AppLogin'), 'AppLogin', 'high');
const AppRegister = createLazy(() => import('./pages/app/AppRegister'), 'AppRegister', 'high');
const AppWelcome = createLazy(() => import('./pages/app/AppWelcome'), 'AppWelcome', 'high');
const AppOnboarding = createLazy(() => import('./pages/app/AppOnboarding'), 'AppOnboarding', 'high');
const AppDashboard = createLazy(() => import('./pages/app/AppDashboard'), 'AppDashboard', 'high');
const AppHelp = createLazy(() => import('./pages/app/AppHelp'), 'AppHelp', 'low');
const AppSettings = createLazy(() => import('./pages/app/AppSettings'), 'AppSettings', 'low');
const AppNotifications = createLazy(() => import('./pages/app/AppNotifications'), 'AppNotifications', 'low');
const AppProducts = createLazy(() => import('./pages/app/AppProducts'), 'AppProducts');
const AppProductsModeration = createLazy(() => import('./pages/app/AppProductsModeration'), 'AppProductsModeration');
const AppPlots = createLazy(() => import('./pages/app/AppPlots'), 'AppPlots');
const AppLots = createLazy(() => import('./pages/app/AppLots'), 'AppLots');
const AppCreateLot = createLazy(() => import('./pages/app/AppCreateLot'), 'AppCreateLot');
const AppLotDetail = createLazy(() => import('./pages/app/AppLotDetail'), 'AppLotDetail');
const AppHubs = createLazy(() => import('./pages/app/AppHubs'), 'AppHubs');
const AppHubDetail = createLazy(() => import('./pages/app/AppHubDetail'), 'AppHubDetail');
const AppOrders = createLazy(() => import('./pages/app/AppOrders'), 'AppOrders');
const AppCodOrders = createLazy(() => import('./pages/app/AppCodOrders'), 'AppCodOrders');
const AppPayments = createLazy(() => import('./pages/app/AppPayments'), 'AppPayments');
const AppMarketplace = createLazy(() => import('./pages/app/AppMarketplace'), 'AppMarketplace');
const AppDeliveryAddresses = createLazy(() => import('./pages/app/AppDeliveryAddresses'), 'AppDeliveryAddresses');
const AppBuyRequests = createLazy(() => import('./pages/app/AppBuyRequests'), 'AppBuyRequests');
const AppNewBuyRequest = createLazy(() => import('./pages/app/AppNewBuyRequest'), 'AppNewBuyRequest');
const AppUsers = createLazy(() => import('./pages/app/AppUsers'), 'AppUsers');
const AppReports = createLazy(() => import('./pages/app/AppReports'), 'AppReports');
const AppTraceability = createLazy(() => import('./pages/app/AppTraceability'), 'AppTraceability');
const AppAudit = createLazy(() => import('./pages/app/AppAudit'), 'AppAudit');
const AppCooperative = createLazy(() => import('./pages/app/AppCooperative'), 'AppCooperative');
const AppRolesPermissions = createLazy(() => import('./pages/app/AppRolesPermissions'), 'AppRolesPermissions');
const AppPlatformSettings = createLazy(() => import('./pages/app/AppPlatformSettings'), 'AppPlatformSettings');
const AppSubscription = createLazy(() => import('./pages/app/AppSubscription'), 'AppSubscription');
const PricingPage = createLazy(() => import('./pages/PricingPage'), 'PricingPage');

interface LazyRouteProps {
  children: React.ReactNode;
  variant?: 'default' | 'products' | 'auth' | 'dashboard' | 'content';
}

const LazyRoute: React.FC<LazyRouteProps> = ({ children, variant = 'default' }) => (
  <Suspense fallback={<SmartPageLoader variant={variant} />}>{children}</Suspense>
);

function App() {
  const location = useLocation();

  useEffect(() => {
    const timer = setTimeout(() => {
      adaptiveLoader.preloadForRoute(location.pathname);
    }, 100);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const prefetchedRoutes = new Set<string>();
    const prefetchLinkRoute = (target: EventTarget | null) => {
      if (!(target instanceof Element)) return;
      const link = target.closest('a[href]');
      if (!link) return;
      const href = link.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
      let routePath = href;
      try {
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) return;
        routePath = `${url.pathname}${url.search}`;
      } catch { return; }
      if (routePath === location.pathname || prefetchedRoutes.has(routePath)) return;
      prefetchedRoutes.add(routePath);
      adaptiveLoader.preloadForRoute(routePath);
    };

    const onPointerOver = (event: Event) => prefetchLinkRoute(event.target);
    const onFocus = (event: FocusEvent) => prefetchLinkRoute(event.target);
    const onTouchStart = (event: TouchEvent) => prefetchLinkRoute(event.target);

    document.addEventListener('pointerover', onPointerOver, { passive: true });
    document.addEventListener('focusin', onFocus, { passive: true });
    document.addEventListener('touchstart', onTouchStart, { passive: true });

    return () => {
      document.removeEventListener('pointerover', onPointerOver);
      document.removeEventListener('focusin', onFocus);
      document.removeEventListener('touchstart', onTouchStart);
    };
  }, [location.pathname]);

  return (
    <OptimizedProviders>
      <div className="min-h-screen bg-background">
        <SkipLink />
        <Suspense fallback={<SmartPageLoader variant="default" />}>
          <Routes>
            {/* ============ VITRINE: agrinabiso.com ============ */}
            <Route path="/" element={<SiteLayout><OptimizedHomePage /></SiteLayout>} />
            <Route path="/auth" element={<LazyRoute variant="auth"><SiteLayout><AuthPage /></SiteLayout></LazyRoute>} />
            <Route path="/auth/callback" element={<LazyRoute variant="auth"><SiteLayout><AuthCallback /></SiteLayout></LazyRoute>} />
            
            {/* Pages stratégiques */}
            <Route path="/solution" element={<LazyRoute variant="content"><SiteLayout><SolutionPage /></SiteLayout></LazyRoute>} />
            <Route path="/producteurs" element={<LazyRoute variant="content"><SiteLayout><ProducteursPage /></SiteLayout></LazyRoute>} />
            <Route path="/acheteurs" element={<LazyRoute variant="content"><SiteLayout><AcheteursPage /></SiteLayout></LazyRoute>} />
            <Route path="/partenaires" element={<LazyRoute variant="content"><SiteLayout><PartenairesPage /></SiteLayout></LazyRoute>} />
            <Route path="/cooperatives" element={<LazyRoute variant="content"><SiteLayout><CooperativesPage /></SiteLayout></LazyRoute>} />
            
            {/* Pages informatives */}
            <Route path="/about" element={<LazyRoute variant="content"><SiteLayout><About /></SiteLayout></LazyRoute>} />
            <Route path="/contact" element={<LazyRoute variant="content"><SiteLayout><Contact /></SiteLayout></LazyRoute>} />
            <Route path="/faq" element={<LazyRoute variant="content"><SiteLayout><FAQ /></SiteLayout></LazyRoute>} />
            <Route path="/sitemap" element={<LazyRoute variant="content"><SiteLayout><Sitemap /></SiteLayout></LazyRoute>} />
            
            {/* Pages légales */}
            <Route path="/legal" element={<LazyRoute variant="content"><SiteLayout><LegalNotice /></SiteLayout></LazyRoute>} />
            <Route path="/privacy" element={<LazyRoute variant="content"><SiteLayout><Privacy /></SiteLayout></LazyRoute>} />
            <Route path="/cookies" element={<LazyRoute variant="content"><SiteLayout><Cookies /></SiteLayout></LazyRoute>} />
            <Route path="/conditions" element={<LazyRoute variant="content"><SiteLayout><TermsOfUse /></SiteLayout></LazyRoute>} />
            <Route path="/terms-of-sale" element={<LazyRoute variant="content"><SiteLayout><TermsOfSale /></SiteLayout></LazyRoute>} />
            <Route path="/refund-policy" element={<LazyRoute variant="content"><SiteLayout><RefundPolicy /></SiteLayout></LazyRoute>} />

            {/* ============ REDIRECTIONS LEGACY ============ */}
            <Route path="/landing/farmers" element={<Navigate to="/producteurs" replace />} />
            <Route path="/landing/buyers" element={<Navigate to="/acheteurs" replace />} />
            <Route path="/landing/investors" element={<Navigate to="/partenaires" replace />} />
            <Route path="/pour-les-producteurs" element={<Navigate to="/producteurs" replace />} />
            <Route path="/pour-les-acheteurs" element={<Navigate to="/acheteurs" replace />} />
            <Route path="/pour-les-investisseurs" element={<Navigate to="/partenaires" replace />} />
            <Route path="/services" element={<Navigate to="/solution" replace />} />
            <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/dashboard/*" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/farmer" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/admin" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/smart-farming" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/business" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/advanced" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/analytics" element={<Navigate to="/app/reports" replace />} />
            <Route path="/marketplace" element={<Navigate to="/app/marketplace" replace />} />
            <Route path="/products" element={<Navigate to="/app/products" replace />} />
            <Route path="/products/:id" element={<Navigate to="/app/products" replace />} />
            <Route path="/product/:id" element={<Navigate to="/app/products" replace />} />
            <Route path="/checkout" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/orders" element={<Navigate to="/app/orders" replace />} />
            <Route path="/training" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/training/*" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/ai-assistant" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/ai" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/ai-features" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/weather" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/help" element={<Navigate to="/app/help" replace />} />
            <Route path="/settings" element={<Navigate to="/app/settings" replace />} />
            <Route path="/profile" element={<Navigate to="/app/settings" replace />} />
            <Route path="/favorites" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/download" element={<Navigate to="/" replace />} />
            <Route path="/testimonials" element={<Navigate to="/about" replace />} />
            <Route path="/success-stories" element={<Navigate to="/about" replace />} />
            <Route path="/impact" element={<Navigate to="/about" replace />} />
            <Route path="/how-it-works" element={<Navigate to="/solution" replace />} />
            <Route path="/partners" element={<Navigate to="/partenaires" replace />} />
            <Route path="/tarifs" element={<LazyRoute><PricingPage /></LazyRoute>} />
            <Route path="/pricing" element={<Navigate to="/tarifs" replace />} />
            <Route path="/blog" element={<Navigate to="/" replace />} />
            <Route path="/blog/*" element={<Navigate to="/" replace />} />
            <Route path="/crowdfunding" element={<Navigate to="/" replace />} />
            <Route path="/support" element={<Navigate to="/app/help" replace />} />
            <Route path="/support/*" element={<Navigate to="/app/help" replace />} />
            <Route path="/theme-demo" element={<Navigate to="/" replace />} />
            <Route path="/waitlist/*" element={<Navigate to="/" replace />} />
            <Route path="/magic-auth/*" element={<Navigate to="/auth" replace />} />

            {/* ============ APP: Business Platform ============ */}
            <Route path="/app/welcome" element={<LazyRoute variant="auth"><AppWelcome /></LazyRoute>} />
            <Route path="/app/login" element={<LazyRoute variant="auth"><AppLogin /></LazyRoute>} />
            <Route path="/app/register" element={<LazyRoute variant="auth"><AppRegister /></LazyRoute>} />
            <Route path="/app/onboarding" element={<LazyRoute variant="auth"><AppOnboarding /></LazyRoute>} />

            <Route path="/app" element={<AppLayout />}>
              <Route index element={<Navigate to="/app/dashboard" replace />} />
              <Route path="dashboard" element={<LazyRoute variant="dashboard"><AppDashboard /></LazyRoute>} />
              <Route path="settings" element={<LazyRoute variant="dashboard"><AppSettings /></LazyRoute>} />
              <Route path="notifications" element={<LazyRoute variant="dashboard"><AppNotifications /></LazyRoute>} />
              <Route path="help" element={<LazyRoute variant="content"><AppHelp /></LazyRoute>} />
              <Route path="products" element={<LazyRoute variant="dashboard"><AppProducts /></LazyRoute>} />
              <Route path="products-moderation" element={<LazyRoute variant="dashboard"><AppProductsModeration /></LazyRoute>} />
              <Route path="plots" element={<LazyRoute variant="dashboard"><AppPlots /></LazyRoute>} />
              <Route path="lots" element={<LazyRoute variant="dashboard"><AppLots /></LazyRoute>} />
              <Route path="lots/new" element={<LazyRoute variant="dashboard"><AppCreateLot /></LazyRoute>} />
              <Route path="lots/:id" element={<LazyRoute variant="dashboard"><AppLotDetail /></LazyRoute>} />
              <Route path="hubs" element={<LazyRoute variant="dashboard"><AppHubs /></LazyRoute>} />
              <Route path="hubs/:id" element={<LazyRoute variant="dashboard"><AppHubDetail /></LazyRoute>} />
              <Route path="orders" element={<LazyRoute variant="dashboard"><AppOrders /></LazyRoute>} />
              <Route path="cod-orders" element={<LazyRoute variant="dashboard"><AppCodOrders /></LazyRoute>} />
              <Route path="payments" element={<LazyRoute variant="dashboard"><AppPayments /></LazyRoute>} />
              <Route path="marketplace" element={<LazyRoute variant="dashboard"><AppMarketplace /></LazyRoute>} />
              <Route path="addresses" element={<LazyRoute variant="dashboard"><AppDeliveryAddresses /></LazyRoute>} />
              <Route path="buy-requests" element={<LazyRoute variant="dashboard"><AppBuyRequests /></LazyRoute>} />
              <Route path="buy-requests/new" element={<LazyRoute variant="dashboard"><AppNewBuyRequest /></LazyRoute>} />
              <Route path="users" element={<LazyRoute variant="dashboard"><AppUsers /></LazyRoute>} />
              <Route path="reports" element={<LazyRoute variant="dashboard"><AppReports /></LazyRoute>} />
              <Route path="traceability" element={<LazyRoute variant="dashboard"><AppTraceability /></LazyRoute>} />
              <Route path="audit" element={<LazyRoute variant="dashboard"><AppAudit /></LazyRoute>} />
              <Route path="cooperative" element={<LazyRoute variant="dashboard"><AppCooperative /></LazyRoute>} />
              <Route path="roles" element={<LazyRoute variant="dashboard"><AppRolesPermissions /></LazyRoute>} />
              <Route path="platform-settings" element={<LazyRoute variant="dashboard"><AppPlatformSettings /></LazyRoute>} />
              <Route path="subscription" element={<LazyRoute variant="dashboard"><AppSubscription /></LazyRoute>} />
              <Route path="*" element={<LazyRoute variant="dashboard"><AppDashboard /></LazyRoute>} />
            </Route>

            {/* Catch all */}
            <Route path="*" element={<LazyRoute><SiteLayout><NotFoundPage /></SiteLayout></LazyRoute>} />
          </Routes>
        </Suspense>
      </div>
    </OptimizedProviders>
  );
}

export default App;
